import { FC, ReactNode } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

export type SchrittStatus = 'erledigt' | 'offen' | 'automatisch' | 'warnung' | 'gesperrt';

const STIL: Record<
  SchrittStatus,
  { farbe: string; bg: string; text: string; Icon: typeof CheckCircleIcon }
> = {
  erledigt: { farbe: '#1e6b3a', bg: '#f0faf3', text: 'Erledigt', Icon: CheckCircleIcon },
  offen: { farbe: '#a12a1f', bg: '#ffffff', text: 'Offen', Icon: RadioButtonUncheckedIcon },
  automatisch: { farbe: '#1d4f91', bg: '#f5f9ff', text: 'Läuft automatisch', Icon: AutorenewIcon },
  warnung: { farbe: '#8a4b00', bg: '#fffaf0', text: 'Bitte prüfen', Icon: WarningAmberIcon },
  gesperrt: {
    farbe: '#6b7280',
    bg: '#f9fafb',
    text: 'Erst nach vorherigem Schritt',
    Icon: LockOutlinedIcon,
  },
};

/**
 * Ein Schritt im Webinar-Fahrplan: Nummer, Titel, Status, ein Satz was zu tun ist, und EIN Knopf, der direkt
 * dorthin führt. Details (z. B. die Hindernisliste) als kurze Zeilen darunter.
 */
const SchrittKarte: FC<{
  nummer?: number;
  titel: string;
  status: SchrittStatus;
  /** Ein Satz: was ist hier zu tun bzw. was ist der Stand. */
  beschreibung: ReactNode;
  details?: ReactNode[];
  aktion?: { label: string; onClick: () => void };
  /** Zusätzliche Elemente rechts (z. B. Link zur Live-Seite). */
  rechts?: ReactNode;
  statusText?: string;
}> = ({ nummer, titel, status, beschreibung, details, aktion, rechts, statusText }) => {
  const s = STIL[status];
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1,
        bgcolor: s.bg,
        borderColor: status === 'offen' ? '#f5c2bd' : '#e5e7eb',
        borderLeft: `4px solid ${s.farbe}`,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      <s.Icon sx={{ color: s.farbe, mt: '2px' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 15 }}>
            {nummer !== undefined ? `${nummer}. ` : ''}
            {titel}
          </Typography>
          <Typography variant="caption" sx={{ color: s.farbe, fontWeight: 600 }}>
            {statusText ?? s.text}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {beschreibung}
        </Typography>
        {details && details.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {details.map((d, i) => (
              <Typography key={i} component="li" variant="caption" color="text.secondary">
                {d}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
        {rechts}
        {aktion && (
          <Button
            size="small"
            variant={status === 'offen' || status === 'warnung' ? 'contained' : 'outlined'}
            onClick={aktion.onClick}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {aktion.label}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default SchrittKarte;
